/**
 * Shared display formatters.
 *
 * These render the same numbers and labels in the editor, the live preview,
 * and the cover — drift between per-file copies shows up to agents as
 * inconsistent pricing display, so the single implementations live here.
 * (The compact "$1.2M" money formats intentionally differ per surface and
 * stay local to their components.)
 */

/** Full price with thousands separators: 512300 → "$512,300"; junk → "". */
export const formatPrice = (/** @type {unknown} */ n) => {
  const v = Number(n);
  return v && !Number.isNaN(v) ? `$${Math.round(v).toLocaleString()}` : "";
};

// Words that stay lowercase inside a title. Capitalising every word turned
// "bedrooms_and_bathrooms" into "Bedrooms And Bathrooms" on section headings an
// agent shows a seller, which reads like a machine wrote it.
const MINOR_TITLE_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "per",
  "the",
  "to",
  "vs",
]);

/** Field key → Title Case label: "suggested_range_low" → "Suggested Range Low",
 * "bedrooms_and_bathrooms" → "Bedrooms and Bathrooms". */
export const humanizeKey = (/** @type {string | null | undefined} */ key) =>
  (key || "")
    .replace(/_/g, " ")
    .split(" ")
    .map((word, i) =>
      // The first word is always capitalised — "of the record" is a label, not
      // a sentence fragment, so it must not open lowercase.
      i > 0 && MINOR_TITLE_WORDS.has(word.toLowerCase())
        ? word.toLowerCase()
        : word.replace(/^\w/, (/** @type {string} */ c) => c.toUpperCase())
    )
    .join(" ");

/** Enum-shaped values ({value: "High"}) or plain strings → display string. */
export const enumValue = (/** @type {unknown} */ v) =>
  v && typeof v === "object" && "value" in v
    ? String(/** @type {{value?: unknown}} */ (v).value || "")
    : String(v || "");
